import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock } from "@phosphor-icons/react";
import axios from "axios";

const API = "/api";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t("auth_passwords_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success(t("auth_reset_success"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6">
        <div className="text-center">
          <Lock weight="fill" className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">Invalid or missing reset token.</p>
          <Button
            onClick={() => navigate("/login")}
            className="mt-4 bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041]"
            data-testid="back-to-login-btn"
          >
            {t("auth_back_to_login")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Lock weight="fill" className="w-10 h-10 text-[#F5A524] mx-auto mb-3" />
          <h1 className="font-['Outfit'] text-2xl font-semibold text-white">
            {t("auth_reset_title")}
          </h1>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <p className="text-emerald-400">{t("auth_reset_success")}</p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold"
              data-testid="login-after-reset"
            >
              {t("nav_login")}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                {t("auth_new_password")}
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                {t("auth_confirm_password")}
              </Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold active:scale-[0.98] transition-all"
              data-testid="reset-submit"
            >
              {loading ? t("loading") : t("auth_reset_btn")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
